import type { Variants } from 'motion/react'

export const MOTION = {
  duration: {
    sm: 0.15,
    base: 0.25,
    lg: 0.35
  },
  ease: {
    out: [0.16, 1, 0.3, 1]
  }
} as const

const enter = { duration: MOTION.duration.base, ease: MOTION.ease.out }
const enterSm = { duration: MOTION.duration.sm, ease: MOTION.ease.out }

export const fadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: enter }
}

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: enterSm }
}

export const crossfade: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: enterSm }
}

// A side rail that slides in/out from its own edge (e.g. ingest's DetailRail, anchored right)
// rather than fading or sliding vertically like fadeSlideUp. `exit="hidden"` reuses the same
// offset as the entrance point, so it retraces its own path back out.
export const slideInFromRight: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: enter }
}

const STAGGER_STEP = 0.05
const STAGGER_TOTAL_CAP = 0.4

export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: STAGGER_STEP, delayChildren: STAGGER_STEP }
  }
}

export const staggerItem: Variants = fadeSlideUp

// For CSS grid layouts, where items must stay direct children of the grid container to
// keep their track placement — wrapping them in a staggerContainer parent would collapse
// them into a single grid cell. Bakes the same per-item delay directly into each item's own
// variant instead of relying on a parent's staggerChildren orchestration.
export function fadeSlideUpDelay(index: number): Variants {
  return {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { ...enter, delay: index * STAGGER_STEP } }
  }
}

// motion/react's gesture props (onAnimationStart/End, onDrag*) collide with the DOM event
// handlers of the same name on any HTML attributes type — apply this when wrapping a native
// element's prop type for use with `m.*` (m.button, m.span, etc.) to drop just those keys.
export type MotionSafe<T> = Omit<T, 'onAnimationStart' | 'onAnimationEnd' | 'onDrag' | 'onDragStart' | 'onDragEnd'>

// A fixed per-item delay scales linearly with list length — fine for a handful of items,
// sluggish for a long one. This caps the total reveal time instead, so a 50-item list
// staggers as tightly as a 10-item one rather than taking 5x as long to finish.
export function staggerContainerForCount(itemCount: number): Variants {
  const step = itemCount > 1 ? Math.min(STAGGER_STEP, STAGGER_TOTAL_CAP / (itemCount - 1)) : STAGGER_STEP
  return {
    hidden: {},
    visible: {
      transition: { staggerChildren: step, delayChildren: STAGGER_STEP }
    }
  }
}

const SLIDE_DISTANCE = 24

// Direction-aware step transition (wizards, paginated flows). Pass a `custom` prop of 1
// (forward) or -1 (back) to both AnimatePresence and the animated child — motion resolves
// these function variants against whichever direction was current at enter vs. exit time,
// so a step entering from the right can cross an exiting step leaving to the left, or the
// reverse, without either side needing its own separate variant object.
export const slideVariants: Variants = {
  initial: (direction: number) => ({ opacity: 0, x: direction * SLIDE_DISTANCE }),
  animate: { opacity: 1, x: 0, transition: enter },
  // pointerEvents: 'none' rules out a second click landing on the outgoing step's own
  // submit button during its brief exit window — that click already fired and started
  // this navigation; the fading step underneath shouldn't be able to fire it again.
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -SLIDE_DISTANCE,
    pointerEvents: 'none',
    transition: enterSm
  })
}

// For a small overlay mounted at initial="visible", animated to "hidden" — appears at full
// tint immediately (no fade in, so it reads as an instant confirmation) and fades away over
// duration.lg. opacity isn't transform, so it's outside what reducedMotion="user" strips; a
// brief tint isn't the kind of movement that guidance targets.
export const successFlash: Variants = {
  visible: { opacity: 0.6 },
  hidden: { opacity: 0, transition: { duration: MOTION.duration.lg, ease: MOTION.ease.out } }
}

// x is a transform, so reducedMotion="user" collapses this to the settled (0) position —
// the shake itself is exactly the kind of movement that setting is meant to suppress.
export const shake: Variants = {
  shake: {
    x: [0, -6, 6, -6, 6, 0],
    transition: { duration: MOTION.duration.base, ease: MOTION.ease.out }
  }
}
